import { useEffect, useRef, useState } from "react";
import ReactCrop from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ImageIcon, Upload, Crop as CropIcon, Loader2, RotateCcw } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const DEFAULT_ICON = "/smartfx-logo.png";

export const BrandingTab = () => {
  const imgRef = useRef(null);
  const [current, setCurrent] = useState(null);   // saved ai_icon_url (relative)
  const [src, setSrc] = useState(null);           // data url of file being cropped
  const [crop, setCrop] = useState();
  const [completed, setCompleted] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/branding");
      setCurrent(data?.ai_icon_url || null);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const onSelectFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Image too large. Max 5 MB."); return; }
    const reader = new FileReader();
    reader.addEventListener("load", () => {
      setSrc(reader.result?.toString() || null);
      setCrop(undefined);
      setCompleted(null);
    });
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const uploadCrop = async () => {
    const image = imgRef.current;
    if (!image || !completed?.width || !completed?.height) {
      toast.error("Select a crop area first.");
      return;
    }
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.floor(completed.width * scaleX));
    canvas.height = Math.max(1, Math.floor(completed.height * scaleY));
    const ctx = canvas.getContext("2d");
    ctx.drawImage(
      image,
      completed.x * scaleX, completed.y * scaleY,
      completed.width * scaleX, completed.height * scaleY,
      0, 0, canvas.width, canvas.height,
    );
    const blob = await new Promise((res) => canvas.toBlob(res, "image/png"));
    if (!blob) { toast.error("Could not process the image."); return; }
    const form = new FormData();
    form.append("file", blob, "ai-icon.png");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/config/ai-icon", form,
        { headers: { "Content-Type": "multipart/form-data" } });
      setCurrent(data.ai_icon_url);
      setSrc(null);
      toast.success("brand icon updated — it now shows for all users.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    setBusy(true);
    try {
      await api.delete("/admin/config/ai-icon");
      setCurrent(null);
      toast.success("Reset to the default Bull icon.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const previewSrc = current ? `${BACKEND}${current}` : DEFAULT_ICON;

  return (
    <div className="space-y-6" data-testid="branding-tab">
      <div className="bg-[#0F0F0F] border border-[#222] p-5 rounded-sm">
        <div className="flex items-center gap-2 mb-4">
          <ImageIcon className="h-4 w-4 text-white" />
          <span className="font-heading font-semibold">Floating Brand Icon</span>
        </div>
        <p className="text-sm text-[#888] mb-5">
          Upload and crop the floating brand icon that appears on every client's dashboard while Smart Scalper is running.
          Changes apply globally.
        </p>

        {/* Current icon preview */}
        <div className="flex items-center gap-4 mb-6">
          <div className="h-16 w-16 rounded-full flex items-center justify-center overflow-hidden shrink-0"
            style={{ background: "radial-gradient(circle at 50% 40%, #0b1a2e, #060a14)", border: "2px solid #3b82f6", boxShadow: "0 0 24px rgba(59,130,246,.45)" }}>
            <img src={previewSrc} alt="Current brand icon" data-testid="branding-current-icon"
              className="h-full w-full object-contain" style={{ transform: "scale(1.0)" }} />
          </div>
          <div>
            <div className="text-sm font-semibold">Current icon</div>
            <div className="text-xs text-[#888]">{current ? "Custom uploaded icon" : "Default Bull logo"}</div>
          </div>
        </div>

        {/* File input */}
        <label data-testid="branding-file-label"
          className="inline-flex items-center gap-2 cursor-pointer px-4 py-2.5 rounded-sm bg-white text-black text-sm font-semibold hover:opacity-90 transition-opacity">
          <Upload className="h-4 w-4" /> Choose Image
          <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={onSelectFile}
            data-testid="branding-file-input" className="hidden" />
        </label>
        {current && (
          <Button variant="outline" onClick={reset} disabled={busy} data-testid="branding-reset-btn"
            className="ml-3 border-[#333] rounded-sm text-sm">
            <RotateCcw className="h-4 w-4 mr-1.5" /> Reset to default
          </Button>
        )}
      </div>

      {/* Cropper */}
      {src && (
        <div className="bg-[#0F0F0F] border border-[#222] p-5 rounded-sm" data-testid="branding-cropper">
          <div className="flex items-center gap-2 mb-4">
            <CropIcon className="h-4 w-4 text-white" />
            <span className="font-heading font-semibold">Crop your image</span>
            <span className="text-xs text-[#888] ml-2">Drag to select any area — free crop.</span>
          </div>
          <div className="max-w-full overflow-auto bg-black/40 p-3 rounded-sm inline-block">
            <ReactCrop crop={crop} onChange={(_px, percent) => setCrop(percent)} onComplete={(c) => setCompleted(c)}>
              <img ref={imgRef} src={src} alt="To crop" style={{ maxHeight: 340 }} />
            </ReactCrop>
          </div>
          <div className="flex items-center gap-3 mt-4">
            <Button onClick={uploadCrop} disabled={busy} data-testid="branding-save-btn"
              className="rounded-sm bg-white text-black font-semibold hover:opacity-90">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Icon"}
            </Button>
            <Button variant="outline" onClick={() => setSrc(null)} disabled={busy}
              data-testid="branding-cancel-btn" className="border-[#333] rounded-sm">
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
